<script setup lang="ts">
  import { computed } from "vue";
  import { OrderItem } from "@src/components";
  import { useOrderListStore, useExecutorListStore, useWidgetsStore } from "@src/store";
  import { filterOrders } from "@src/utils";
  import { useCarousel, useProfileOptions, useScrollDirection } from "@src/hooks";
  import { MainPageActionCheckPairs, MainPageActionOrders, MainPageActionAlarming } from "./Actions";

  const vertical_scroll = useScrollDirection("vertical");
  const horizontal_scroll = useScrollDirection("horizontal");
  const orderListStore = useOrderListStore();
  const executorListStore = useExecutorListStore();
  const widgets_store = useWidgetsStore();
  const { profile } = useProfileOptions();

  const filteredOrderList = computed(() => {
    const orders = orderListStore.ids.map((orderId) => orderListStore.map[orderId]);
    const historyOrders = orderListStore.historyIds.map((orderId) => orderListStore.map[orderId]);

    const filtered = filterOrders([...orders, ...historyOrders], "new", profile.value.current_user.user_id, []);

    return filtered;
  });

  const {
    listRef,
    wrapperRef,
    state: carouselState,
    handleStart,
    handleMove,
    handleEnd,
    getItemStyle,
  } = useCarousel({
    itemCount: computed(() => filteredOrderList.value.length),
    scale: false,
    gap: 16,
  });

  // Логика для точек (максимум 5 видимых, все точки отрисованы)
  const maxDots = 5;
  const dotSize = 6; // размер точки
  const dotGap = 8; // отступ между точками
  const dotWidth = dotSize + dotGap; // общая ширина точки с отступом

  // Вычисление смещения для контейнера точек
  const dotTranslateX = computed(() => {
    const itemsCount = filteredOrderList.value.length;
    if (itemsCount <= maxDots) return 0;

    const centerPosition = Math.floor(maxDots / 2); // 2

    // До достижения индекса 3 (0, 1, 2) смещение не нужно - видим точки 0-4
    if (carouselState.activeIndex <= centerPosition) return 0;

    // Смещаем так, чтобы активная точка была в центре (позиция 2 из 5 видимых)
    let targetStartIndex = carouselState.activeIndex - centerPosition;

    // Но не можем начать позже, чем (itemsCount - maxDots)
    // Это нужно чтобы последняя точка была видна
    const maxStartIndex = itemsCount - maxDots;
    targetStartIndex = Math.min(targetStartIndex, maxStartIndex);

    // Вычисляем смещение для этого startIndex
    const translate = targetStartIndex * dotWidth;

    return -translate;
  });

  const handleTouchStart = (e: TouchEvent) => {
    horizontal_scroll.start(e);
    handleStart(e);
  };

  const handleTouchMove = (e: TouchEvent) => {
    horizontal_scroll.move(e);
    handleMove(e);
  };

  const handleTouchEnd = (e: TouchEvent) => {
    horizontal_scroll.end(e);
    handleEnd(e);
  };
</script>

<template>
  <div
    ref="containerRef"
    class="main-page-menu-store"
    data-testid="main-page-menu-store"
    @touchstart="vertical_scroll.start"
    @touchmove="vertical_scroll.move"
    @touchend="vertical_scroll.end"
  >
    <div v-if="filteredOrderList.length > 0" ref="wrapperRef" class="main-page-menu-store__orders-carousel">
      <div
        ref="listRef"
        class="main-page-menu-store__orders"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <OrderItem
          v-for="(order, index) in filteredOrderList"
          :key="order.order_id"
          :is_short="true"
          :style="getItemStyle(index)"
          :order_status="order.status"
          :order_type="order.order_type"
          :order_total_items="order.goods.reduce((acc, good) => acc + good.quantity, 0)"
          :order_place_from="order.place_from"
          :store="order.place_from_type"
          :order_place_section="order.place_section"
          :order_place_to="order.place_to"
          :order_number="order.order_number?.toUpperCase() || ''"
          :createdAt="order.created"
          :executerDisplayName="executorListStore.map[order.executed_by]?.display_name"
          :partial="order.partial"
          :comment="order.comment"
          @click="widgets_store.open('order_card', { order_id: order.order_id })"
        />
      </div>
      <div class="main-page-menu-store__dots">
        <div
          class="main-page-menu-store__dots-container-wrapper"
          :style="filteredOrderList.length < 5 ? { justifyContent: 'center' } : {}"
        >
          <div class="main-page-menu-store__dots-container" :style="{ transform: `translateX(${dotTranslateX}px)` }">
            <span
              v-for="(order, index) in filteredOrderList"
              :key="order.order_id"
              class="main-page-menu-store__dot"
              :class="{ active: index === carouselState.activeIndex }"
            />
          </div>
        </div>
      </div>
    </div>
    <MainPageActionCheckPairs />
    <MainPageActionAlarming />
    <MainPageActionOrders />
  </div>
</template>

<style scoped>
  .main-page-menu-store {
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
    width: 100%;
    overflow-y: auto;
    padding: 16px 16px 96px;
  }

  .main-page-menu-store__orders-carousel {
    overflow: hidden;
    user-select: none;
    flex-shrink: 0;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }

  .main-page-menu-store__orders {
    display: flex;
    gap: 16px;
    transition: transform 0.4s cubic-bezier(0.2, 1.25, 0.6, 1);
    will-change: transform;
  }

  .main-page-menu-store__orders > * {
    flex: 0 0 100%;
    box-sizing: border-box;
  }

  .main-page-menu-store__dots {
    display: flex;
    justify-content: center;
    width: 100%;
    margin-top: 8px;
  }

  .main-page-menu-store__dots-container-wrapper {
    overflow: hidden;
    width: calc(6px * 5 + 8px * 4); /* 5 точек по 6px + 4 gap по 8px = 62px */
    display: flex;
    justify-content: flex-start;
  }

  .main-page-menu-store__dots-container {
    display: flex;
    gap: 8px;
    transition: transform 0.4s ease;
  }

  .main-page-menu-store__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--sf-base-border-dark);
    flex-shrink: 0;
    transition:
      opacity 0.4s ease,
      transform 0.4s ease;
    opacity: 0.4;

    &.active {
      opacity: 1;
    }
  }
</style>
